import { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Select, Spin, Switch, message } from 'antd';
import {
    getFeatureSettingByType,
    updateFeatureSettingByType,
    type FeatureSetting,
} from '../../api/SystemFeatureSetting';
import { FEATURE_SETTING_TYPES } from '../../config/appointmentTransition';
import { mixGetV1 } from '../../services/mixin';
import { chipBackground, readableTagColor, tagShortName, type TagOption } from './types';
import useIsDark from './useIsDark';
import { MUTED_TEXT_BUTTON, brandButton } from './brand';

interface Props {
    open: boolean;
    canEdit: boolean;
    /** Toàn bộ thẻ, để tra tên của thẻ đang cấu hình kể cả khi nó không còn được chọn */
    tagsById: Record<number, TagOption>;
    /** Thẻ được phép chọn làm trạng thái đích */
    selectableTags: TagOption[];
    onClose: () => void;
}

/** Giá trị đang sửa của một tính năng */
interface Draft {
    originTagIds: number[];
    customerTagId: number | null;
    syncCustomer: boolean;
    /** Nhân viên Trực page / Telesale mặc định (appointment_default_staff) */
    staffId: number | null;
    telesaleId: number | null;
    isActive: boolean;
}

function toDraft(setting: FeatureSetting | null): Draft {
    const cfg = setting?.config;
    return {
        originTagIds: Array.isArray(cfg?.origin_customer_tag_ids) ? cfg!.origin_customer_tag_ids.map(Number) : [],
        customerTagId: cfg?.customer_tag_id ?? cfg?.target_customer_tag_id ?? null,
        syncCustomer: cfg?.sync_customer ?? true,
        staffId: cfg?.staff_id ? Number(cfg.staff_id) : null,
        telesaleId: cfg?.telesale_id ? Number(cfg.telesale_id) : null,
        isActive: setting ? setting.is_active === 1 : false,
    };
}

interface StaffOption {
    value: number;
    label: string;
}

/** Nhân sự server trả kèm cấu hình, để vẫn hiện tên khi người đó không có trong danh sách đã tải */
function settingStaff(setting: FeatureSetting | null): StaffOption[] {
    return [setting?.default_staff, setting?.default_telesale]
        .filter((s): s is NonNullable<typeof s> => !!s?.id)
        .map((s) => ({ value: s.id, label: s.name }));
}

/** Danh sách nhân viên theo loại phòng ban: 2 Trực page, 3 Telesale (như form Tạo khách hàng) */
async function loadStaffOptions(typeDepartmentId: number): Promise<StaffOption[]> {
    const res = await mixGetV1('/api/v1/be/staffs', { limit: 200, type_department_id: typeDepartmentId }).catch(() => null);
    return res?.status && res.data?.items ? res.data.items.map((u: any) => ({ value: u.id, label: u.name })) : [];
}

/** Thứ tự thẻ nguồn không quan trọng */
const sameIds = (a: number[], b: number[]) =>
    a.length === b.length && [...a].sort((x, y) => x - y).every((v, i) => v === [...b].sort((x, y) => x - y)[i]);

function sameDraft(a: Draft, b: Draft): boolean {
    return (
        sameIds(a.originTagIds, b.originTagIds) &&
        a.customerTagId === b.customerTagId &&
        a.syncCustomer === b.syncCustomer &&
        a.staffId === b.staffId &&
        a.telesaleId === b.telesaleId &&
        a.isActive === b.isActive
    );
}

/** Thông tin thẻ trả kèm cấu hình (customer_tag, origin_customer_tags) — dùng khi thẻ chưa có trong danh sách đã tải */
function settingTags(setting: FeatureSetting | null): TagOption[] {
    if (!setting) return [];
    return [...(setting.origin_customer_tags || []), ...(setting.customer_tag ? [setting.customer_tag] : [])].map(
        (t) => ({ id: t.id, name: t.name, code: t.code ?? null, color: t.color ?? null })
    );
}

const FIELD_LABEL =
    'text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-[#8a8a8a] mb-1';

const filterTag = (input: string, option?: { searchText?: string }) =>
    String(option?.searchText ?? '')
        .toLowerCase()
        .includes(input.toLowerCase());

/** Cài đặt tự động theo hành động (chuyển trạng thái S khi check-in, nhân viên mặc định...), dùng chung toàn hệ thống. */
export default function FeatureSettingsDrawer({
    open,
    canEdit,
    tagsById,
    selectableTags,
    onClose,
}: Props) {
    const isDark = useIsDark();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [baseline, setBaseline] = useState<Record<string, Draft>>({});
    const [drafts, setDrafts] = useState<Record<string, Draft>>({});
    /** Tính năng chưa từng được cấu hình trên server */
    const [missing, setMissing] = useState<Record<string, boolean>>({});
    /** Thẻ server trả kèm cấu hình, để vẫn hiện đúng nhãn khi thẻ không có trong `tagsById` */
    const [knownTags, setKnownTags] = useState<Record<number, TagOption>>({});
    /** Danh sách chọn nhân viên Trực page / Telesale; tải một lần khi mở lần đầu */
    const [pageStaffs, setPageStaffs] = useState<StaffOption[]>([]);
    const [telesales, setTelesales] = useState<StaffOption[]>([]);
    const [knownStaff, setKnownStaff] = useState<StaffOption[]>([]);

    const load = async () => {
        setLoading(true);
        try {
            const needStaff = FEATURE_SETTING_TYPES.some((f) => f.staff) && !pageStaffs.length && !telesales.length;
            const [results, staffLists] = await Promise.all([
                Promise.all(FEATURE_SETTING_TYPES.map((f) => getFeatureSettingByType(f.type))),
                needStaff ? Promise.all([loadStaffOptions(2), loadStaffOptions(3)]) : null,
            ]);
            if (staffLists) {
                setPageStaffs(staffLists[0]);
                setTelesales(staffLists[1]);
            }
            const base: Record<string, Draft> = {};
            const notFound: Record<string, boolean> = {};
            const known: Record<number, TagOption> = {};
            const staffKnown: StaffOption[] = [];
            FEATURE_SETTING_TYPES.forEach((f, i) => {
                const res = results[i];
                const setting = res.status && res.data ? res.data : null;
                base[f.type] = toDraft(setting);
                notFound[f.type] = !setting;
                settingTags(setting).forEach((t) => (known[t.id] = t));
                staffKnown.push(...settingStaff(setting));
            });
            setBaseline(base);
            setDrafts(JSON.parse(JSON.stringify(base)));
            setMissing(notFound);
            setKnownTags(known);
            setKnownStaff(staffKnown);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) load();
    }, [open]);

    const changedTypes = useMemo(
        () =>
            FEATURE_SETTING_TYPES.filter(
                (f) => drafts[f.type] && baseline[f.type] && !sameDraft(drafts[f.type], baseline[f.type])
            ).map((f) => f.type),
        [drafts, baseline]
    );
    const dirty = changedTypes.length > 0;

    const patch = (type: string, next: Partial<Draft>) =>
        setDrafts((prev) => ({ ...prev, [type]: { ...prev[type], ...next } }));

    const save = async () => {
        for (const type of changedTypes) {
            const f = FEATURE_SETTING_TYPES.find((x) => x.type === type)!;
            const d = drafts[type];
            if (!d.isActive) continue;
            if (f.target && !d.customerTagId) {
                message.warning(`Chọn trạng thái đích cho "${f.name}" trước khi bật`);
                return;
            }
            if (f.staff && !d.staffId && !d.telesaleId) {
                message.warning(`Chọn ít nhất một nhân viên cho "${f.name}" trước khi bật`);
                return;
            }
        }

        setSaving(true);
        try {
            for (const type of changedTypes) {
                const f = FEATURE_SETTING_TYPES.find((x) => x.type === type)!;
                const d = drafts[type];
                const res = await updateFeatureSettingByType(type, {
                    ...(f.origin ? { origin_customer_tag_ids: d.originTagIds } : {}),
                    ...(f.target ? { customer_tag_id: d.customerTagId, sync_customer: d.syncCustomer } : {}),
                    ...(f.staff ? { staff_id: d.staffId, telesale_id: d.telesaleId } : {}),
                    is_active: d.isActive ? 1 : 0,
                    // Lần đầu lưu thì server tạo bản ghi mới, gửi kèm tên để không bị trống
                    ...(missing[type]
                        ? { name: FEATURE_SETTING_TYPES.find((f) => f.type === type)?.name }
                        : {}),
                });
                if (!res.status) {
                    message.error(res.msg || 'Lưu cấu hình thất bại');
                    return;
                }
            }
            message.success('Đã lưu cài đặt tự động');
            await load();
        } finally {
            setSaving(false);
        }
    };

    /** Nhân viên đang được cấu hình có thể không còn trong danh sách (đổi phòng ban...); vẫn hiện để không mất tên */
    const staffOptions = (list: StaffOption[], currentId: number | null) =>
        currentId && !list.some((s) => s.value === currentId)
            ? [...list, knownStaff.find((s) => s.value === currentId) ?? { value: currentId, label: `#${currentId}` }]
            : list;

    const tagOptions = (currentIds: (number | null)[]) => {
        const list = [...selectableTags];
        // Thẻ đang cấu hình có thể không thuộc luồng lịch hẹn; vẫn hiện để không mất nhãn
        currentIds.forEach((id) => {
            const tag = id ? tagsById[id] ?? knownTags[id] : undefined;
            if (tag && !list.some((t) => t.id === id)) list.push(tag);
        });
        return list.map((t) => ({
            value: t.id,
            searchText: `${t.code ?? ''} ${t.name}`,
            label: (
                <span className="inline-flex items-center gap-2 min-w-0">
                    {t.code && (
                        <span
                            className="font-mono text-[11px] px-1.5 py-0.5 rounded shrink-0"
                            style={{
                                backgroundColor: chipBackground(t.color, isDark),
                                color: readableTagColor(t.color, isDark),
                            }}
                        >
                            {t.code}
                        </span>
                    )}
                    <span className="truncate">{tagShortName(t, t.id)}</span>
                </span>
            ),
        }));
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            width={460}
            title="Cài đặt tự động"
            footer={
                canEdit && (
                    <div className="flex items-center gap-2">
                        <Button
                            type="text"
                            disabled={!dirty || saving}
                            className={MUTED_TEXT_BUTTON}
                            onClick={() => setDrafts(JSON.parse(JSON.stringify(baseline)))}
                        >
                            Hoàn tác
                        </Button>
                        <span className="flex-1" />
                        <Button onClick={onClose}>Đóng</Button>
                        <Button
                            type="primary"
                            loading={saving}
                            disabled={!dirty}
                            onClick={save}
                            className={brandButton(dirty)}
                        >
                            Lưu cài đặt
                        </Button>
                    </div>
                )
            }
        >
            <p className="text-xs text-gray-500 dark:text-[#a0a0a0] mb-4">
                Tự động chuyển trạng thái S của khách và gán nhân viên mặc định khi thực hiện một thao tác. Áp dụng
                chung cho mọi phòng ban và màn hình, không phụ thuộc phạm vi đang chọn trên ma trận.
            </p>

            <Spin spinning={loading}>
                <div className="flex flex-col gap-3">
                    {FEATURE_SETTING_TYPES.map((f) => {
                        const d = drafts[f.type];
                        if (!d) return <div key={f.type} className="h-24" />;
                        const changed = changedTypes.includes(f.type);
                        return (
                            <section
                                key={f.type}
                                className={`rounded-lg border px-4 py-3.5 bg-white dark:bg-[#141414] ${
                                    changed
                                        ? 'border-orange-300 dark:border-orange-500/60'
                                        : 'border-gray-200 dark:border-[#2a2a2a]'
                                }`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">
                                            {f.name}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-[#a0a0a0] mt-0.5">
                                            {f.description}
                                        </div>
                                        {missing[f.type] && (
                                            <div className="text-[11px] text-orange-500 mt-1">
                                                Chưa được cấu hình — lưu lần đầu sẽ tạo mới.
                                            </div>
                                        )}
                                    </div>
                                    <Switch
                                        checked={d.isActive}
                                        disabled={!canEdit}
                                        onChange={(v) => patch(f.type, { isActive: v })}
                                        className={d.isActive ? '!bg-[#3e91ab]' : ''}
                                    />
                                </div>

                                <div className={`mt-3 flex flex-col gap-3 ${d.isActive ? '' : 'opacity-60'}`}>
                                    {f.origin && (
                                        <div>
                                            <div className={FIELD_LABEL}>{f.origin.label}</div>
                                            <Select
                                                mode="multiple"
                                                className="w-full"
                                                placeholder={f.origin.placeholder}
                                                allowClear
                                                showSearch
                                                disabled={!canEdit}
                                                value={d.originTagIds}
                                                onChange={(v) => patch(f.type, { originTagIds: v })}
                                                options={tagOptions(d.originTagIds)}
                                                filterOption={filterTag}
                                            />
                                        </div>
                                    )}

                                    {f.target && (
                                        <>
                                            <div>
                                                <div className={FIELD_LABEL}>Chuyển sang trạng thái</div>
                                                <Select
                                                    className="w-full"
                                                    placeholder="Chọn trạng thái đích"
                                                    allowClear
                                                    showSearch
                                                    disabled={!canEdit}
                                                    value={d.customerTagId ?? undefined}
                                                    onChange={(v) => patch(f.type, { customerTagId: v ?? null })}
                                                    options={tagOptions([d.customerTagId])}
                                                    filterOption={filterTag}
                                                />
                                            </div>

                                            <label className="flex items-center justify-between gap-3 cursor-pointer">
                                                <span className="text-xs text-gray-600 dark:text-gray-300">
                                                    Đồng bộ trạng thái sang hồ sơ khách hàng
                                                </span>
                                                <Switch
                                                    size="small"
                                                    checked={d.syncCustomer}
                                                    disabled={!canEdit}
                                                    onChange={(v) => patch(f.type, { syncCustomer: v })}
                                                    className={d.syncCustomer ? '!bg-[#3e91ab]' : ''}
                                                />
                                            </label>
                                        </>
                                    )}

                                    {f.staff && (
                                        <>
                                            <div>
                                                <div className={FIELD_LABEL}>Nhân viên Trực page</div>
                                                <Select
                                                    className="w-full"
                                                    placeholder="Chọn nhân viên Trực page"
                                                    allowClear
                                                    showSearch
                                                    optionFilterProp="label"
                                                    disabled={!canEdit}
                                                    value={d.staffId ?? undefined}
                                                    onChange={(v) => patch(f.type, { staffId: v ?? null })}
                                                    options={staffOptions(pageStaffs, d.staffId)}
                                                />
                                            </div>
                                            <div>
                                                <div className={FIELD_LABEL}>Nhân viên Telesale</div>
                                                <Select
                                                    className="w-full"
                                                    placeholder="Chọn nhân viên Telesale"
                                                    allowClear
                                                    showSearch
                                                    optionFilterProp="label"
                                                    disabled={!canEdit}
                                                    value={d.telesaleId ?? undefined}
                                                    onChange={(v) => patch(f.type, { telesaleId: v ?? null })}
                                                    options={staffOptions(telesales, d.telesaleId)}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </section>
                        );
                    })}
                </div>
            </Spin>
        </Drawer>
    );
}
